/**
 * JOB HUNTER › PIPELINE — /job-hunter/pipeline
 *
 * Every application past the decision, as a board: saved → preparing → needs
 * input → ready → applied → interview → offer, plus closed. Drag a card to move
 * it; dropping a saved job into a preparing column approves it (the agent
 * drafts the application).
 */
import { DragEvent, useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { CircleQuestionMark, GripVertical, MessageCircle } from 'lucide-react';
import { JobHunterShell, useRunFinished } from '@/components/job-hunter/JobHunterShell';
import { ApplicationDrawer } from '@/components/job-hunter/ApplicationDrawer';
import { AgentChip, Pill, ScoreRing } from '@/components/job-hunter/ui';
import { useJobHunter } from '@/lib/job-hunter/store';
import { flag, timeAgo } from '@/lib/job-hunter/format';
import { jobHunterService, type JhApplication, type JhStatus } from '@/services/job-hunter.service';

const COLUMNS: Array<{ key: string; label: string; statuses: JhStatus[]; drop: JhStatus; tone: string; hint: string }> = [
  { key: 'saved', label: 'Saved', statuses: ['saved'], drop: 'saved', tone: 'var(--color-info)', hint: 'Parked for later' },
  { key: 'approved', label: 'Preparing', statuses: ['approved'], drop: 'approved', tone: 'var(--color-warning)', hint: 'Approved — drafting' },
  { key: 'needs_input', label: 'Needs input', statuses: ['needs_input'], drop: 'needs_input', tone: 'var(--color-warning)', hint: 'Questions only you can answer' },
  { key: 'ready', label: 'Ready to submit', statuses: ['ready'], drop: 'ready', tone: 'var(--color-success)', hint: 'Cover note + answers done' },
  { key: 'applied', label: 'Applied', statuses: ['applied'], drop: 'applied', tone: 'var(--color-info)', hint: 'Submitted on the employer site' },
  { key: 'interview', label: 'Interview', statuses: ['interview'], drop: 'interview', tone: 'var(--color-success)', hint: 'In process' },
  { key: 'offer', label: 'Offer', statuses: ['offer'], drop: 'offer', tone: 'var(--color-success)', hint: '🎉' },
  { key: 'closed', label: 'Closed', statuses: ['rejected', 'withdrawn'], drop: 'rejected', tone: 'var(--color-border-strong)', hint: 'Rejected or withdrawn' },
];
const ALL = COLUMNS.flatMap((c) => c.statuses).join(',');
const lastMove = (a: JhApplication) => a.history[a.history.length - 1]?.at ?? a.updatedAt;

export default function JhPipeline() {
  const { profileUid, agents } = useJobHunter();
  const agentOf = (uid?: string) => (uid && (agents?.length ?? 0) > 1 ? agents?.find((a) => a.agentUid === uid) : undefined);
  const [rows, setRows] = useState<JhApplication[] | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!profileUid) return;
    try { setRows((await jobHunterService.applications({ profileUid, status: ALL, sort: 'updated', limit: 100 })).rows); }
    catch { setRows([]); }
  }, [profileUid]);
  useEffect(() => { void load(); }, [load]);
  useRunFinished(load);

  const grouped = useMemo(() => Object.fromEntries(COLUMNS.map((c) => [c.key, (rows ?? []).filter((r) => c.statuses.includes(r.status))])), [rows]);

  const move = async (uid: string, col: (typeof COLUMNS)[number]) => {
    const a = rows?.find((r) => r.applicationUid === uid);
    if (!a || col.statuses.includes(a.status)) return;
    const prev = rows;
    setRows((list) => list?.map((r) => (r.applicationUid === uid ? { ...r, status: col.drop } : r)) ?? null);
    try {
      if (a.status === 'saved' && ['approved', 'needs_input', 'ready'].includes(col.drop)) {
        await jobHunterService.decide(uid, 'apply');
        toast.success('Approved — the agent prepared the application');
      } else {
        await jobHunterService.updateApplication(uid, { status: col.drop });
        toast.success(`Moved to ${col.label}`);
      }
      await load();
    } catch { setRows(prev); }
  };

  const onDrop = (col: (typeof COLUMNS)[number]) => (e: DragEvent) => {
    e.preventDefault();
    setOver(null);
    const uid = e.dataTransfer.getData('text/plain');
    if (uid) void move(uid, col);
  };

  return (
    <JobHunterShell section="Pipeline" description="Applications from approval to offer — drag cards between stages">
      {rows === null ? (
        <div className="jh-kanban">{COLUMNS.slice(0, 5).map((c) => <div key={c.key} className="h-96 animate-pulse rounded-2xl bg-surface-alt" />)}</div>
      ) : (
        <div className="jh-kanban">
          {COLUMNS.map((col) => (
            <section key={col.key} className={clsx('jh-col', over === col.key && 'is-over')}
              onDragOver={(e) => { e.preventDefault(); if (over !== col.key) setOver(col.key); }}
              onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setOver(null); }}
              onDrop={onDrop(col)}>
              <div className="jh-col-head">
                <span className="inline-flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: col.tone }} />{col.label}</span>
                <span className="jh-chip">{grouped[col.key]?.length ?? 0}</span>
              </div>
              <div className="px-3 pb-1 text-[11px] text-text-muted">{col.hint}</div>
              <div className="jh-col-body">
                {(grouped[col.key] ?? []).map((a) => (
                  <div key={a.applicationUid} className={clsx('jh-kcard', dragging === a.applicationUid && 'is-dragging')} draggable
                    onDragStart={(e) => { e.dataTransfer.setData('text/plain', a.applicationUid); e.dataTransfer.effectAllowed = 'move'; setDragging(a.applicationUid); }}
                    onDragEnd={() => setDragging(null)} onClick={() => setOpen(a.applicationUid)}>
                    <div className="flex items-start gap-2.5">
                      <ScoreRing score={a.evaluation.fitScore} size={36} stroke={4} />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-[13px] font-semibold leading-snug">{a.job?.title}</div>
                        <div className="mt-0.5 truncate text-[12px] text-text-secondary">{flag(a.evaluation.country ?? a.job?.country)} {a.job?.employerName}</div>
                      </div>
                      <GripVertical size={14} className="mt-0.5 shrink-0 text-text-muted" />
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px]">
                      <span className="jh-chip">#{a.ref}</span>
                      {agentOf(a.agentUid) && <AgentChip name={agentOf(a.agentUid)!.name} color={agentOf(a.agentUid)!.color} size="xs" />}
                      {a.status === 'needs_input' && a.draft?.pendingQuestions.length ? <Pill tone="warning" icon={<CircleQuestionMark size={11} />}>{a.draft.pendingQuestions.length} question{a.draft.pendingQuestions.length === 1 ? '' : 's'}</Pill> : null}
                      {a.decisionChannel === 'whatsapp' && <Pill tone="success" icon={<MessageCircle size={11} />}>WhatsApp</Pill>}
                      {a.jobClosed && <Pill tone="danger">Closed</Pill>}
                      <span className="ml-auto text-text-muted">{timeAgo(lastMove(a))}</span>
                    </div>
                  </div>
                ))}
                {!(grouped[col.key] ?? []).length && <div className="grid flex-1 place-items-center rounded-xl border border-dashed border-border py-8 text-[11.5px] text-text-muted">Drop here</div>}
              </div>
            </section>
          ))}
        </div>
      )}
      <ApplicationDrawer applicationUid={open} onClose={() => setOpen(null)} onChanged={() => void load()} />
    </JobHunterShell>
  );
}
